import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { http, money } from "../lib/api";
import { useCart, useAuth } from "../lib/ctx";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Package } from "lucide-react";

export function Checkout() {
  const { items, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    fulfilment: "delivery", customer_email: user?.email || "", customer_name: user?.name || "", customer_phone: "",
    line1: "", line2: "", city: "", postcode: "", collection_slot: "", delivery_notes: "", discount_code: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  if (items.length === 0) return <div className="max-w-3xl mx-auto p-10 text-center"><p className="text-white/60 mb-4">Your basket is empty.</p><Link to="/shop" className="oh-btn-lime px-6 py-3 rounded-full text-xs tracking-widest uppercase inline-block">Continue Shopping</Link></div>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/checkout/create-session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        fulfilment: f.fulfilment,
        delivery_address: f.fulfilment === "delivery" ? { line1: f.line1, line2: f.line2, city: f.city, postcode: f.postcode } : {},
        collection_slot: f.fulfilment === "collection" ? f.collection_slot : null,
        delivery_notes: f.delivery_notes,
        discount_code: f.discount_code || null,
        customer_email: f.customer_email, customer_name: f.customer_name, customer_phone: f.customer_phone,
      });
      clear();
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-14">
      <h1 className="font-serif text-5xl mb-8" data-testid="checkout-title">Checkout</h1>
      <form onSubmit={submit} className="space-y-8">
        <div className="oh-card p-6 space-y-4">
          <h2 className="font-serif text-2xl">Contact Details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Full name" className="oh-input" value={f.customer_name} onChange={set("customer_name")} data-testid="co-name"/>
            <input required type="email" placeholder="Email" className="oh-input" value={f.customer_email} onChange={set("customer_email")} data-testid="co-email"/>
            <input placeholder="Phone" className="oh-input" value={f.customer_phone} onChange={set("customer_phone")} data-testid="co-phone"/>
          </div>
        </div>
        <div className="oh-card p-6 space-y-4">
          <h2 className="font-serif text-2xl">Fulfilment</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {["delivery","collection"].map((v) => (
              <button type="button" key={v} onClick={()=>set("fulfilment")(v)} data-testid={`fulfil-${v}`} className={`p-4 rounded-xl border text-left ${f.fulfilment===v?"border-lime-400 bg-lime-500/10":"border-[var(--oh-border)]"}`}>
                <div className="font-serif text-xl capitalize">{v}</div>
                <div className="text-xs text-white/60 mt-1">{v==="delivery"?"UK-wide, from £4.95 (free over £40)":"Collect in-store, choose a slot"}</div>
              </button>
            ))}
          </div>
          {f.fulfilment === "delivery" ? (
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <input required placeholder="Address line 1" className="oh-input" value={f.line1} onChange={set("line1")} data-testid="co-line1"/>
              <input placeholder="Line 2 (optional)" className="oh-input" value={f.line2} onChange={set("line2")}/>
              <input required placeholder="City" className="oh-input" value={f.city} onChange={set("city")} data-testid="co-city"/>
              <input required placeholder="Postcode" className="oh-input" value={f.postcode} onChange={set("postcode")} data-testid="co-postcode"/>
            </div>
          ) : (
            <input required type="datetime-local" className="oh-input" value={f.collection_slot} onChange={set("collection_slot")} data-testid="co-slot"/>
          )}
          <textarea placeholder="Notes for delivery / collection" className="oh-input" rows={2} value={f.delivery_notes} onChange={set("delivery_notes")}/>
        </div>
        <div className="oh-card p-6 space-y-3">
          <h2 className="font-serif text-2xl">Discount Code</h2>
          <input placeholder="e.g. WELCOME10" className="oh-input" value={f.discount_code} onChange={set("discount_code")} data-testid="co-discount"/>
          <div className="text-xs text-white/50">The site-wide 10% is already applied. Codes stack on top only where allowed.</div>
        </div>
        <button disabled={busy} data-testid="co-pay" className="oh-btn-lime w-full py-4 rounded-full text-sm tracking-widest uppercase disabled:opacity-50">{busy ? "Redirecting to Stripe…" : "Pay Securely with Stripe →"}</button>
        <p className="text-xs text-center text-white/40">You'll be redirected to Stripe. Apple Pay & Google Pay supported. We never store card details.</p>
      </form>
    </div>
  );
}

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [tries, setTries] = useState(0);
  useEffect(() => {
    const sid = sp.get("session_id"); if (!sid) return;
    const poll = async () => {
      const { data } = await http.get(`/checkout/status/${sid}`);
      setStatus(data);
      if (data.payment_status !== "paid" && tries < 15) setTimeout(() => setTries((t) => t + 1), 1500);
    };
    poll();
  }, [sp, tries]);
  const paid = status.payment_status === "paid";
  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      {paid ? <CheckCircle2 size={72} className="mx-auto text-lime-400 mb-6"/> : <Package size={72} className="mx-auto text-teal-400 mb-6 animate-pulse"/>}
      <h1 className="font-serif text-5xl" data-testid="pay-status">{paid ? "Order Confirmed" : "Confirming Payment…"}</h1>
      {paid && (
        <>
          <p className="text-white/70 mt-4">Thank you. Your order <span className="font-mono text-lime-400">{status.order_number}</span> has been received.</p>
          <p className="text-sm text-white/50 mt-2">A confirmation email will be sent shortly. You can track progress in your account.</p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/account" className="oh-btn-lime px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">View My Orders</Link>
            <Link to="/shop" className="oh-btn-ghost px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">Continue Shopping</Link>
          </div>
        </>
      )}
      {!paid && <p className="text-white/60 mt-4">Please wait while we confirm your payment with Stripe…</p>}
    </div>
  );
}
